export type TrickleVentsSemanticPosition = 'sash' | 'head' | 'custom' | 'extender';

/** Top frame extender depth applied when user selects Extender trickle vent mode. */
export const TRICKLE_VENT_EXTENDER_TOP_MM = 20;

export function hasActiveTopFrameExtender(extTopMm: string | number | null | undefined): boolean {
  const n = Number(String(extTopMm ?? '').trim());
  return Number.isFinite(n) && n > 0;
}

/** Trickle Extender mode is only valid when a top frame extender depth is set. */
export function isExtenderTrickleVentActive(
  position: string | null | undefined,
  extTopMm: string | number | null | undefined,
): boolean {
  return parseSemanticTricklePosition(position) === 'extender' && hasActiveTopFrameExtender(extTopMm);
}

/** When top frame extender is off, Extender trickle selection must not remain active. */
export function resolveTricklePositionAfterTopExtenderRemoved(
  position: string | null | undefined,
  extTopMm: string | number | null | undefined,
): TrickleVentsSemanticPosition {
  if (parseSemanticTricklePosition(position) === 'extender' && !hasActiveTopFrameExtender(extTopMm)) {
    return 'head';
  }
  return parseSemanticTricklePosition(position);
}

export function formatTrickleVentsPositionLabel(pos: string | null | undefined): string {
  switch (String(pos || '').toLowerCase()) {
    case 'sash':
      return 'In Sash';
    case 'head':
      return 'In Head';
    case 'extender':
      return 'Extender';
    case 'custom':
      return 'Custom';
    default:
      return 'Yes';
  }
}

export function parseSemanticTricklePosition(raw: string | null | undefined): TrickleVentsSemanticPosition {
  const p = String(raw || '').toLowerCase();
  if (p === 'custom') return 'custom';
  if (p === 'head') return 'head';
  if (p === 'extender') return 'extender';
  return 'sash';
}

/** Maps accepted semantic position to WG presentation ventPos. */
export function semanticTricklePositionToVentPos(pos: string): 'sash' | 'jamb' | 'extender' | 'none' {
  const p = String(pos || '').toLowerCase();
  if (p === 'head') return 'jamb';
  if (p === 'extender') return 'extender';
  if (p === 'sash') return 'sash';
  return 'none';
}

export function isJambLikeVentPos(vp: string | null | undefined): boolean {
  const v = String(vp || '').toLowerCase();
  return v === 'jamb' || v === 'extender';
}

/** Ensures top extender is at least TRICKLE_VENT_EXTENDER_TOP_MM when Extender mode is chosen. */
export function ensureExtenderTopMm(currentTopMm: string | number | null | undefined): string {
  const n = Number(String(currentTopMm ?? '').trim());
  if (!Number.isFinite(n) || n < TRICKLE_VENT_EXTENDER_TOP_MM) return String(TRICKLE_VENT_EXTENDER_TOP_MM);
  return String(n);
}
